import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import ConfettiExplosion from 'react-confetti-explosion';

import { useQueryParams } from 'hooks/useQueryParams';
import YesNoModal from 'components/Modals/yesNoModal';
import { FreecodeLoading } from 'components/FreecodePage/FreecodeLoading/FreecodeLoading';
import FreecodePage from 'components/FreecodePage/FreecodePage';
import { useFreecodeContextLayout, useFreecodeContextRun, useFreecodeContextData } from 'hooks/useContext';
import FreecodeLayout from 'components/FreecodePage/FreecodeLayout/FreecodeLayout';
import { identifiers, editorTypes } from 'components/FreecodePage/FreecodeLayout/FreecodeLayoutConstants';
import FreecodeDiscussion from 'components/FreecodePage/FreecodeTabs/FreecodeDiscussion/FreecodeDiscussion';
import FreecodeCourse from 'components/FreecodePage/FreecodeTabs/FreecodeCourse/FreecodeCourse';
import FreecodeMobilePromptModal from 'components/Modals/FreecodeModals/FreecodeMobilePromptModal';
import useExerciseAutoSave from './useExerciseAutoSave';
import useExerciseRunFunction from './useExerciseRunFunction';
import useExerciseLoadFunction from './useExerciseLoadFunction';
import { sendAnalytics } from 'functions/sendAnalytics';
import { ConfettiContainer } from './styles';

const HEADER_LINKS = [{ link: '/exercises', label: 'All Exercises' }];

export default function FreecodeExercise() {
  const { exerciseId } = useParams();
  const { courseId } = useQueryParams();

  const [exerciseComponents, setExerciseComponents] = useState({});
  const [showNextLevelFlowToRunModal, setShowNextLevelFlowToRunModal] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);

  const { setEditorType } = useFreecodeContextLayout();
  const { levelToRunRef, updateAndRunFlow } = useFreecodeContextRun();
  const { testImpl } = useFreecodeContextData();
  const { loading, error } = useExerciseLoadFunction(exerciseId, courseId);

  useExerciseAutoSave();
  useExerciseRunFunction({ setShowNextLevelFlowToRunModal, setShowConfetti });

  useEffect(() => {
    setEditorType(editorTypes.exercise);
  }, [setEditorType]);

  useEffect(() => {
    if (!exerciseId) return;
    sendAnalytics('openExercise', exerciseId);
    setExerciseComponents((e) => ({
      ...e,
      [identifiers.discussion]: <FreecodeDiscussion testId={exerciseId} />,
    }));
  }, [exerciseId, courseId]);

  useEffect(() => {
    if (courseId) {
      setExerciseComponents((e) => ({
        ...e,
        [identifiers.course]: <FreecodeCourse />,
      }));
    }
  }, [courseId]);

  // Open the flow overlay on load if exercise has a flow but it should not open if user came from course page
  useEffect(() => {
    if (!testImpl?.parsedFlow?.[levelToRunRef.current] || loading) return;
    if (!localStorage.getItem('fsCourse-flow-opened-on-pathname')?.includes('courses'))
      updateAndRunFlow(levelToRunRef.current);
    localStorage.setItem('fsCourse-flow-opened-on-pathname', window.location.pathname);
  }, [loading, levelToRunRef, testImpl?.parsedFlow, updateAndRunFlow]);

  return (
    <FreecodePage headerLinks={HEADER_LINKS}>
      <FreecodeLoading loading={loading} errors={[error]} errorLinkText="All Exercises" errorLink="/exercises" />
      {!loading && <FreecodeLayout parentComponents={exerciseComponents} />}
      <FreecodeMobilePromptModal type="exercise" id={exerciseId} />
      {testImpl?.parsedFlow?.[0] && showNextLevelFlowToRunModal && (
        <YesNoModal
          isFreecodeModal
          modalInfo={{
            title: 'Congratulations!',
            body: `Do you want to continue to level ${levelToRunRef.current + 2}?`,
          }}
          confirm={(isConfirmed) => {
            if (isConfirmed) updateAndRunFlow(levelToRunRef.current + 1);
            setShowNextLevelFlowToRunModal(false);
          }}
        />
      )}
      {showConfetti && (
        <ConfettiContainer>
          <ConfettiExplosion
            force={1}
            duration="3000"
            height="200vh"
            particleCount="400"
            onComplete={() => setShowConfetti(false)}
          />
        </ConfettiContainer>
      )}
    </FreecodePage>
  );
}
